import { FC, useEffect, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { Organization } from "@models/organization.model.ts";
import OrgPickerList from "./OrgPickerList";
import { getDescendantIds } from "./orgHierarchy";

interface Props {
  show: boolean;
  org: Organization | null;
  allOrgs: Organization[];
  onClose: () => void;
  onSubmit: (newParentId: number | null) => Promise<void>;
}

const MoveToParentModal: FC<Props> = ({ show, org, allOrgs, onClose, onSubmit }) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setSelectedId(org?.parent_id ?? null);
    }
  }, [show, org]);

  const excludeIds = useMemo(() => {
    if (!org) return new Set<number>();
    const ids = getDescendantIds(org.id, allOrgs);
    ids.add(org.id);
    return ids;
  }, [org, allOrgs]);

  if (!org) return null;

  const currentParent = org.parent_id != null ? allOrgs.find((o) => o.id === org.parent_id) : null;

  const handleSave = async () => {
    setSubmitting(true);
    try {
      await onSubmit(selectedId);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Move to parent</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <div className="text-muted small mb-1">Moving</div>
          <div className="fw-semibold">{org.name}</div>
          <div className="text-muted small mt-2">
            Current parent:{" "}
            {currentParent ? (
              <span className="fw-semibold">{currentParent.name}</span>
            ) : (
              <em>— top-level —</em>
            )}
          </div>
        </div>

        <OrgPickerList
          allOrgs={allOrgs}
          excludeIds={excludeIds}
          selectedId={selectedId}
          onSelect={setSelectedId}
          placeholder="Search for a parent organization…"
          showTopLevelOption
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={submitting || selectedId === (org.parent_id ?? null)}
        >
          {submitting ? "Saving…" : "Move"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MoveToParentModal;
